import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { translate, useLocale } from "../../localization";
import { AppButton, AppTextField } from "../ui";
import Header from "../header/Header";
import TurnstileWidget from "../security/TurnstileWidget";
import { readDemoSession, redeemDemoCode } from "../../services/DemoSessionAPI";
import "./DemoSessionAccess.scss";

const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY?.trim();

export default function DemoSessionAccess({ children }) {
  const locale = useLocale();
  const { sessionId = "" } = useParams();
  const [code, setCode] = useState(/^\d{4}$/.test(sessionId) ? sessionId : "");
  const [proof, setProof] = useState("");
  const [proofError, setProofError] = useState("");
  const [reset, setReset] = useState(0);
  const [grant, setGrant] = useState(null);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const request = useRef(null);
  const onProof = useCallback(value => setProof(value), []);
  const onProofError = useCallback(value => setProofError(value), []);

  useEffect(() => {
    request.current?.abort(); setGrant(null); setReport(null); setMessage(""); setBusy(false);
    setCode(/^\d{4}$/.test(sessionId) ? sessionId : "");
    setProof(""); setProofError(""); setReset(value => value + 1);
    return () => request.current?.abort();
  }, [sessionId]);

  const clearAccess = useCallback(text => {
    request.current?.abort(); setGrant(null); setReport(null); setBusy(false); setMessage(text);
    setProof(""); setProofError(""); setReset(value => value + 1);
  }, []);

  useEffect(() => {
    if (!grant) return undefined;
    const expire = () => clearAccess(translate("This demo session has expired. Demo access lasts 24 hours."));
    const remaining = Date.parse(grant.expiresAt) - Date.now();
    if (remaining <= 0) { expire(); return undefined; }
    const timeout = setTimeout(expire, remaining);
    const checkExpiry = () => { if (Date.parse(grant.expiresAt) <= Date.now()) expire(); };
    window.addEventListener("focus", checkExpiry);
    document.addEventListener("visibilitychange", checkExpiry);
    return () => { clearTimeout(timeout); window.removeEventListener("focus", checkExpiry); document.removeEventListener("visibilitychange", checkExpiry); };
  }, [grant, clearAccess, locale]);

  const loadReport = async access => {
    const controller = new AbortController(); request.current?.abort(); request.current = controller;
    setBusy(true); setMessage("");
    try {
      const next = await readDemoSession(access, controller.signal);
      if (!controller.signal.aborted) setReport(next);
    } catch (error) {
      if (controller.signal.aborted) return;
      if ([404, 410].includes(error.status)) clearAccess(translate("This demo session is unavailable or has expired."));
      else { setReport(null); setMessage(translate("Couldn't load this session. Please try again.")); }
    } finally { if (!controller.signal.aborted) setBusy(false); }
  };

  const submit = async event => {
    event.preventDefault();
    if (busy || !/^\d{4}$/.test(code) || !proof) return;
    const controller = new AbortController(); request.current?.abort(); request.current = controller;
    setBusy(true); setMessage(""); setReport(null);
    try {
      const access = await redeemDemoCode(code, proof, controller.signal);
      if (controller.signal.aborted) return;
      setProof(""); setReset(value => value + 1);
      setGrant(access); await loadReport(access);
    } catch (error) {
      if (controller.signal.aborted) return;
      setMessage(error.status === 429 ? translate("Too many attempts. Please try again in 10 minutes.") :
        error.status === 404 ? translate("Code not found or expired. Check the four digits shown in your headset.") :
        translate("Couldn't open this session. Please try again."));
    } finally {
      if (!controller.signal.aborted) { setBusy(false); setProof(""); setReset(value => value + 1); }
    }
  };

  return <div className="demoSessionAccess">
    <Header><AppButton component={Link} to="/signup" kind="primary">{translate("Start a trial")}</AppButton></Header>
    <main>
      <section className="demoSessionAccess__intro">
        <p className="demoSessionAccess__eyebrow">{translate("10k Science demo session")}</p>
        <h1>{grant ? translate("Your session preview") : translate("View your session")}</h1>
        {!grant ? <>
          <p>{translate("Enter the four-digit code shown in your headset. Your demo report is available for 24 hours after the code is created.")}</p>
          <form onSubmit={submit} className="demoSessionAccess__form">
            <AppTextField label={translate("Session code")} value={code} autoComplete="off"
              onChange={event => setCode(event.target.value.replace(/\D/g, "").slice(0, 4))}
              inputProps={{ inputMode: "numeric", pattern: "[0-9]{4}", maxLength: 4 }} />
            <TurnstileWidget siteKey={siteKey} action="demo_session" resetTrigger={reset} onTokenChange={onProof} onErrorChange={onProofError} />
            {!siteKey && <p role="alert" className="demoSessionAccess__message">{translate("Session sharing is temporarily unavailable.")}</p>}
            {proofError && <p role="alert" className="demoSessionAccess__message">{proofError}</p>}
            <AppButton type="submit" kind="primary" disabled={busy || code.length !== 4 || !proof}>{translate("View session")}</AppButton>
          </form>
        </> : <div className="demoSessionAccess__status">
          <p>{translate("Available until {value0}", { value0: new Date(grant.expiresAt).toLocaleString(locale) })}</p>
          <AppButton kind="secondary" disabled={busy} onClick={() => loadReport(grant)}>{translate("Refresh session")}</AppButton>
          <AppButton kind="ghost" onClick={() => clearAccess("")}>{translate("Enter another code")}</AppButton>
        </div>}
        {busy && <div role="status" className="demoSessionAccess__loading"><CircularProgress size={22} /><span>{translate("Loading session...")}</span></div>}
        {message && <p role="alert" className="demoSessionAccess__message">{message}</p>}
      </section>
      {report && children(report)}
      <aside className="demoSessionAccess__trial">
        <div><h2>{translate("Teaching or leading a class?")}</h2>
          <p>{translate("Start a 10k Science trial to explore the full analytics dashboard and evaluate how session insights can support your learners.")}</p></div>
        <AppButton component={Link} to="/signup" kind="primary">{translate("Start a trial")}</AppButton>
      </aside>
    </main>
  </div>;
}
